import { useState, useEffect, useRef } from "react";
import Hark from "hark";
import { v4 as uuidv4 } from "uuid";

// https://cloud.google.com/speech-to-text/docs/reference/rest/v1/RecognitionConfig
import { GoogleCloudRecognitionConfig } from "./GoogleCloudRecognitionConfig";
import { useRecorderPermission } from "./recordPermissions";
import { flatten, isEmpty } from "lodash";
import { SeverityLevel } from "@microsoft/applicationinsights-web";

import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { updateInternetStatus } from "@store/internetConnectionSlice";
import { InternetStatus, SttSourceType } from "@store/generalTypes";
import { trackDiagnosticException, trackDiagnosticTrace } from "@utils/diagnostics/appInsightsDiagnostics";
import { ANDROID_WEBM_OPUS_MIME_TYPE, NativeMediaRecorder, NativeRecorderDiagnosticEvent } from "./nativeMediaRecorder";

const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
const STT_DIAGNOSTIC_AREA = "stt";
const MIN_VALID_AUDIO_BLOB_SIZE = 1024;
const RECORDER_OPERATION_TIMEOUT = 3000;
const STT_DIAGNOSTIC_EVENTS = new Set([
    "permission:get-user-media:start",
    "permission:get-user-media:success",
    "permission:get-user-media:error",
    "permission:reset-recorder:start",
    "permission:reset-recorder:complete",
    "audio-pipeline:hard-reset:start",
    "audio-pipeline:hard-reset:success",
    "audio-pipeline:hard-reset:error",
    "start:not-ready",
    "start:busy",
    "start:begin",
    "start:recorder-started",
    "start:recording",
    "start:error",
    "audio-context:resume:before",
    "audio-context:resume:after",
    "audio-context:resume:timeout",
    "audio-context:state-change",
    "audio-context:state-change-resume-after",
    "audio-context:state-change-resume-skipped",
    "audio-context:state-change-resume-timeout",
    "document:visibility-change",
    "document:pagehide",
    "hark:create:before",
    "hark:create:after",
    "hark:speaking",
    "hark:stopped-speaking",
    "hark:blob",
    "hark:stale-stopped-speaking",
    "native:start:before",
    "native:start:after",
    "native:start:onstart",
    "native:start:timeout",
    "native:start:error",
    "native:stop:before",
    "native:stop:after",
    "native:stop:onstop",
    "native:stop:error",
    "process:skip-empty-blob",
    "process:skip-tiny-blob",
    "process:tiny-blob-reset-permission",
    "process:tiny-blob-restart-without-permission-reset",
    "process:duplicate-skip",
    "process:begin",
    "process:file-reader-error",
    "process:stale-result",
    "recorder:create",
    "recorder:destroy",
    "recorder:native-support",
    "recorder:reset",
    "recorder:reset-error",
    "recorder:stream-changed",
    "stop:begin",
    "stop:busy",
    "stop:no-recorder",
    "stop:blob",
    "stop:error",
    "stt:request",
    "stt:response",
    "stt:provider-error",
    "stt:all-providers-failed-empty-result",
    "stt:error",
    "volume-monitor:start",
    "full-audio:restart-after-segment",
    "full-audio:restart-after-tiny-blob"
]);

type SttLifecycleState = "idle" | "starting" | "recording" | "stopping" | "processing" | "error" | "destroyed";
type RecorderState = "recording" | "inactive" | "stopped" | "paused" | "destroyed";
interface ActiveSpeechRecorder {
    startRecording: () => Promise<void>;
    stopRecording: () => Promise<unknown>;
    getBlob: () => Promise<Blob | undefined>;
    reset: () => Promise<void>;
    destroy: () => Promise<void>;
    getState: () => Promise<RecorderState | string>;
    getRecorderType?: () => string;
}

export interface ResultType {
    speechBlob?: Blob;
    elapsed: number;
    transcript: string;
    confidence: number;
    intendedWord?: string;
    context?: any;
    source?: SttSourceType;
    technicalFailure?: boolean;
}

export interface UseSpeechToTextTypes {
    continuous?: boolean;
    fullAudio?: boolean;
    crossBrowser?: boolean;
    googleApiKey: string;
    googleCloudRecognitionConfig?: GoogleCloudRecognitionConfig;
    azureApiKey: string;
    azureRegion: string;
    onStartSpeaking?: () => any;
    onStoppedSpeaking?: () => any;
    timeout?: number;
    useLegacyResults?: boolean;
    useOnlyGoogleCloud?: boolean;
    threshold?: number;
    stoppedTalkingThreshold?: number;
    testOnly?: boolean;
    useStopTalking?: boolean;
}

export interface UseSpeechToTextReturnTypes {
    error: string;
    interimResult?: string;
    isRecording: boolean;
    results: ResultType[];
    isReady: boolean;
    processingAttemptId?: string;
    startSpeechToText: (intendedWord?: string, explicitContext?: string[], context?: any) => Promise<void>;
    stopSpeechToText: (
        processAudio?: boolean,
        intendedWord?: string,
        explicitContext?: string[],
        context?: any
    ) => Promise<boolean>;
    destroy: () => void;
    hardResetAudioPipeline: (context?: any) => Promise<boolean>;
    recordingVolume?: number;
    currentDecibels: number;
    stream?: MediaStream;
}

interface ITransaction {
    assessmentId: number;
    assessmentFlow: string;
    payloadStt: any;
    statusCode: number;
    statusText: string;
    responseStt: string;
}

export const useSpeechToText = ({
    fullAudio,
    googleApiKey,
    googleCloudRecognitionConfig,
    azureApiKey,
    azureRegion,
    onStartSpeaking,
    onStoppedSpeaking,
    threshold = 0.8,
    stoppedTalkingThreshold = -60,
    testOnly,
    useStopTalking = true
}: UseSpeechToTextTypes): UseSpeechToTextReturnTypes => {
    const [isRecording, setIsRecording] = useState(false);

    const audioContextRef = useRef<AudioContext>();

    const [results, setResults] = useState<ResultType[]>([]);

    const [error, setError] = useState("");
    const [processingAttemptId, setProcessingAttemptId] = useState<string>();

    const [currentDecibels, setCurrentDecibels] = useState(-100);

    const [vol, setVol] = useState<number>();
    const mediaStream = useRef<MediaStream>();
    const recorderRef = useRef<ActiveSpeechRecorder>();
    const recorderStreamRef = useRef<MediaStream>();
    const recoveredStreamRef = useRef<MediaStream>();
    const lifecycleRef = useRef<SttLifecycleState>("idle");
    const startOperationRef = useRef<Promise<void>>();
    const stopOperationRef = useRef<Promise<Blob | undefined>>();
    const stopRequestedRef = useRef(false);
    const activeRecordingAttemptId = useRef<string>();
    const speechEventsRef = useRef<any>();
    const volumeAnimationFrameRef = useRef<number>();
    const volumeInputRef = useRef<MediaStreamAudioSourceNode>();
    const volumeAnalyserRef = useRef<AnalyserNode>();
    const processedRecordingAttemptIds = useRef<Set<string>>(new Set());
    const audioContextStateRecoveryRef = useRef(false);

    const dispatch = useAppDispatch();

    const appInsights = useAppInsightsContext();

    const { assessmentFlow, assessment, globalSettings } = useAppSelector((x) => {
        return { assessmentFlow: x.assessmentFlow, assessment: x.assessment, globalSettings: x.globalSettings };
    });

    const [stream, createRecorder, resetRecorderPermission] = useRecorderPermission(
        globalSettings.enableSttDiagnostics,
        globalSettings.enableAndroidAudioConstraints
    );

    useEffect(() => {
        recoveredStreamRef.current = stream;
    }, [stream]);

    useEffect(() => {
        if (!navigator?.mediaDevices?.getUserMedia) {
            setError("getUserMedia is not supported on this device/browser :(");
        }
    }, []);

    const shortId = (value?: string): string | undefined => value?.slice(0, 8);

    const summarizeTracks = (tracks?: MediaStreamTrack[]): string | undefined =>
        tracks
            ?.map((track) => `${track.readyState}${track.enabled ? "" : ":off"}${track.muted ? ":muted" : ""}`)
            .join("|");

    const getAndroidNativeRecorderDecision = (): {
        androidLike: boolean;
        enabled: boolean;
        maxTouchPoints: number;
        nativeSupported: boolean;
        platform: string;
        useNativeRecorder: boolean;
    } => {
        const userAgent = navigator.userAgent ?? "";
        const platform = navigator.platform ?? "";
        const userAgentDataPlatform = (navigator as any).userAgentData?.platform ?? "";
        const maxTouchPoints = navigator.maxTouchPoints ?? 0;
        const androidLike =
            /Android/i.test(userAgent) ||
            /Android/i.test(userAgentDataPlatform) ||
            (/Linux/i.test(platform) && /arm/i.test(platform) && maxTouchPoints > 0);
        const enabled = !!globalSettings.enableAndroidNativeRecorder;
        const nativeSupported = NativeMediaRecorder.isSupported();

        return {
            androidLike,
            enabled,
            maxTouchPoints,
            nativeSupported,
            platform,
            useNativeRecorder: enabled && androidLike && nativeSupported
        };
    };

    const getRecorderType = (recorder?: ActiveSpeechRecorder): string =>
        recorder?.getRecorderType?.() ?? "recordrtc-stereo";

    const debugLog = (event: string, data: any = {}): void => {
        if (!STT_DIAGNOSTIC_EVENTS.has(event)) {
            return;
        }

        const context = data.context ?? {};
        const payload = {
            w: data.intendedWord,
            att: shortId(context.recordingAttemptId),
            try: context.tryNumber,
            fb: context.timeoutFallback,
            step: context.stepName,
            rec: data.recorderState ?? data.recorderStateAfterReset,
            rType: data.recorderType,
            android: data.androidLike,
            native: data.nativeSupported,
            touch: data.maxTouchPoints,
            plat: data.platform,
            ac: data.audioContextState,
            sAct: data.streamActive,
            cAct: data.clonedStreamActive,
            trk: summarizeTracks(data.streamTrackStates),
            sId: shortId(data.streamId),
            cTrk: summarizeTracks(data.clonedTrackStates),
            blob: data.blobSize,
            type: data.blobType,
            reset: data.reset,
            pAudio: data.processAudio,
            flow: data.flowName,
            to: data.timeout,
            phr: data.googlePhraseCount,
            boost: data.boost,
            enc: data.googleEncoding,
            bytes: data.audioContentLength,
            el: data.elapsed,
            gSt: data.googleStatus,
            gCnt: data.googleResultCount,
            gTr: data.googleTranscripts
                ?.map((x: { transcript: string; confidence: number }) => `${x.transcript}:${x.confidence}`)
                .join("|"),
            gErr: data.googleError ?? data.googleParseError,
            azSt: data.azureStatus,
            azSkip: data.azureSkipped,
            azTxt: data.azureDisplayText,
            azCnt: data.azureNBestCount,
            azTr: data.azureTranscripts
                ?.map((x: { transcript: string; confidence: number }) => `${x.transcript}:${x.confidence}`)
                .join("|"),
            azErr: data.azureError ?? data.azureParseError,
            err: data.message ?? data.error ?? data.er,
            state: data.state,
            visibility: data.visibilityState,
            recovery: data.recovery,
            active: shortId(data.activeRecordingAttemptId),
            hasRec: data.hasRecorder,
            hasStream: data.hasStream,
            life: data.lifecycle,
            test: data.testOnly
        };

        trackDiagnosticTrace({
            enabled: globalSettings.enableSttDiagnostics,
            area: STT_DIAGNOSTIC_AREA,
            event,
            properties: payload
        });
    };

    const getRecordingAttemptId = (context?: any): string | undefined => context?.recordingAttemptId;

    const isCurrentRecordingAttempt = (context?: any): boolean => {
        const recordingAttemptId = getRecordingAttemptId(context);

        return !recordingAttemptId || activeRecordingAttemptId.current === recordingAttemptId;
    };

    const withTimeout = async <T,>(promise: Promise<T>, timeout: number, message: string): Promise<T> => {
        let timeoutId: number | undefined;

        try {
            return await Promise.race([
                promise,
                new Promise<T>((resolve, reject) => {
                    timeoutId = window.setTimeout(() => reject(new Error(message)), timeout);
                })
            ]);
        } finally {
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        }
    };

    const resumeAudioContextIfNeeded = async (context?: any): Promise<boolean> => {
        const currentAudioContext = audioContextRef.current;

        if (!currentAudioContext || currentAudioContext.state !== "suspended") {
            return currentAudioContext?.state === "running";
        }

        debugLog("audio-context:resume:before", {
            context,
            audioContextState: currentAudioContext.state
        });

        try {
            await withTimeout(currentAudioContext.resume(), 1500, "AudioContext resume timeout");
            const resumedState = audioContextRef.current?.state;
            debugLog("audio-context:resume:after", {
                context,
                audioContextState: resumedState
            });
            return resumedState === "running";
        } catch (er) {
            debugLog("audio-context:resume:timeout", {
                context,
                er,
                audioContextState: currentAudioContext.state
            });
            return false;
        }
    };

    const handleAudioContextStateChange = async (changedAudioContext: AudioContext): Promise<void> => {
        const state = changedAudioContext.state;
        const shouldTryResume =
            state === "suspended" &&
            !audioContextStateRecoveryRef.current &&
            (lifecycleRef.current === "recording" || lifecycleRef.current === "starting");

        debugLog("audio-context:state-change", {
            state,
            lifecycle: lifecycleRef.current,
            recovery: shouldTryResume
        });

        if (!shouldTryResume || audioContextRef.current !== changedAudioContext) {
            if (state === "suspended") {
                debugLog("audio-context:state-change-resume-skipped", {
                    state,
                    lifecycle: lifecycleRef.current
                });
            }

            return;
        }

        audioContextStateRecoveryRef.current = true;

        try {
            await withTimeout(changedAudioContext.resume(), 1500, "AudioContext state-change resume timeout");
            debugLog("audio-context:state-change-resume-after", {
                state: changedAudioContext.state,
                lifecycle: lifecycleRef.current
            });
        } catch (er) {
            debugLog("audio-context:state-change-resume-timeout", {
                state: changedAudioContext.state,
                lifecycle: lifecycleRef.current,
                er
            });
        } finally {
            audioContextStateRecoveryRef.current = false;
        }
    };

    const createAudioContext = (): AudioContext => {
        const context = new AudioContext();
        context.onstatechange = () => {
            void handleAudioContextStateChange(context);
        };

        return context;
    };

    useEffect(() => {
        if (!audioContextRef.current || audioContextRef.current.state === "closed") {
            audioContextRef.current = createAudioContext();
        } else {
            const currentAudioContext = audioContextRef.current;
            currentAudioContext.onstatechange = () => {
                void handleAudioContextStateChange(currentAudioContext);
            };
        }

        const handleVisibilityChange = (): void => {
            debugLog("document:visibility-change", {
                visibilityState: document.visibilityState,
                audioContextState: audioContextRef.current?.state,
                lifecycle: lifecycleRef.current
            });
        };

        const handlePageHide = (): void => {
            debugLog("document:pagehide", {
                visibilityState: document.visibilityState,
                audioContextState: audioContextRef.current?.state,
                lifecycle: lifecycleRef.current
            });
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("pagehide", handlePageHide);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("pagehide", handlePageHide);

            if (audioContextRef.current) {
                audioContextRef.current.onstatechange = null;
            }
        };
    }, []);

    const cleanupVolumeMonitor = (): void => {
        if (volumeAnimationFrameRef.current) {
            window.cancelAnimationFrame(volumeAnimationFrameRef.current);
            volumeAnimationFrameRef.current = undefined;
        }

        volumeInputRef.current?.disconnect();
        volumeAnalyserRef.current?.disconnect();
        volumeInputRef.current = undefined;
        volumeAnalyserRef.current = undefined;

        debugLog("cleanup:volume-monitor");
    };

    const cleanupSpeechEvents = (): void => {
        const speechEvents = speechEventsRef.current;

        speechEvents?.stop?.();
        speechEvents?.off?.("speaking");
        speechEvents?.off?.("stopped_speaking");
        speechEvents?.off?.("volume_change");
        speechEventsRef.current = undefined;

        debugLog("cleanup:hark");
    };

    const cleanupActiveRecordingSession = (): void => {
        cleanupSpeechEvents();
        cleanupVolumeMonitor();

        mediaStream.current = undefined;

        debugLog("cleanup:recording-session");
    };

    const destroyActiveRecorder = async (recorder?: ActiveSpeechRecorder): Promise<void> => {
        const recorderToDestroy = recorder ?? recorderRef.current;

        if (!recorderToDestroy) {
            return;
        }

        if (recorderRef.current === recorderToDestroy) {
            recorderRef.current = undefined;
            recorderStreamRef.current = undefined;
        }

        try {
            debugLog("recorder:destroy", {
                recorderType: getRecorderType(recorderToDestroy)
            });
            await recorderToDestroy.destroy();
        } catch (er) {
            debugLog("stop:error", { er });
        }
    };

    const resetActiveRecorder = async (recorder: ActiveSpeechRecorder, context?: any): Promise<void> => {
        try {
            await recorder.reset();
            debugLog("recorder:reset", {
                context,
                recorderState: await recorder.getState(),
                recorderType: getRecorderType(recorder)
            });
        } catch (er) {
            debugLog("recorder:reset-error", { context, er });
            throw er;
        }
    };

    const createSpeechRecorder = (activeStream: MediaStream, context?: any): ActiveSpeechRecorder | undefined => {
        const nativeRecorderDecision = getAndroidNativeRecorderDecision();

        debugLog("recorder:native-support", {
            context,
            recorderType: nativeRecorderDecision.useNativeRecorder ? "native-media-recorder" : "recordrtc-stereo",
            androidLike: nativeRecorderDecision.androidLike,
            nativeSupported: nativeRecorderDecision.nativeSupported,
            maxTouchPoints: nativeRecorderDecision.maxTouchPoints,
            platform: nativeRecorderDecision.platform,
            streamId: activeStream.id,
            streamActive: activeStream.active
        });

        if (nativeRecorderDecision.useNativeRecorder) {
            return new NativeMediaRecorder(activeStream, (event: NativeRecorderDiagnosticEvent, data = {}) => {
                debugLog(event, {
                    ...data,
                    context,
                    recorderType: "native-media-recorder",
                    streamId: activeStream.id,
                    streamActive: activeStream.active
                });
            });
        }

        return createRecorder(activeStream);
    };

    const stopActiveRecorderToBlob = async ({
        processAudio,
        intendedWord,
        explicitContext,
        context
    }: {
        processAudio?: boolean;
        intendedWord?: string;
        explicitContext?: string[];
        context?: any;
    }): Promise<Blob | undefined> => {
        if (stopOperationRef.current) {
            debugLog("stop:busy", {
                processAudio,
                intendedWord,
                explicitContext,
                context,
                lifecycle: lifecycleRef.current
            });
            return await stopOperationRef.current;
        }

        const operation = (async (): Promise<Blob | undefined> => {
            const recorder = recorderRef.current;

            if (!recorder) {
                debugLog("stop:no-recorder", { processAudio, intendedWord, context, lifecycle: lifecycleRef.current });
                return undefined;
            }

            lifecycleRef.current = "stopping";

            debugLog("stop:begin", {
                processAudio,
                intendedWord,
                explicitContext,
                context,
                lifecycle: lifecycleRef.current,
                recorderState: await recorder.getState(),
                recorderType: getRecorderType(recorder)
            });

            setIsRecording(false);
            cleanupActiveRecordingSession();

            const recorderState = await recorder.getState();

            if (recorderState !== "inactive") {
                await withTimeout(recorder.stopRecording(), RECORDER_OPERATION_TIMEOUT, "Recorder stop timeout");
            }

            const blob = await recorder.getBlob();
            await resetActiveRecorder(recorder, context);
            lifecycleRef.current = "idle";

            debugLog("stop:blob", {
                processAudio,
                intendedWord,
                context,
                lifecycle: lifecycleRef.current,
                blobSize: blob?.size,
                blobType: blob?.type,
                recorderType: getRecorderType(recorder),
                recorderStateAfterReset: await recorder.getState()
            });

            return blob;
        })();

        stopOperationRef.current = operation;

        try {
            return await operation;
        } finally {
            if (stopOperationRef.current === operation) {
                stopOperationRef.current = undefined;
            }
        }
    };

    const startSpeechToText = async (
        intendedWord?: string,
        explicitContext?: string[],
        context?: any
    ): Promise<void> => {
        if (startOperationRef.current) {
            debugLog("start:busy", { intendedWord, context, lifecycle: lifecycleRef.current });
            return await startOperationRef.current;
        }

        if (stopOperationRef.current) {
            await stopOperationRef.current;
        }

        if (lifecycleRef.current === "recording" || lifecycleRef.current === "starting") {
            debugLog("start:busy", { intendedWord, context, lifecycle: lifecycleRef.current });
            return;
        }

        const operation = startSpeechToTextInternal(intendedWord, explicitContext, context);
        startOperationRef.current = operation;

        try {
            await operation;
        } finally {
            if (startOperationRef.current === operation) {
                startOperationRef.current = undefined;
            }
        }
    };

    const startSpeechToTextInternal = async (
        intendedWord?: string,
        explicitContext?: string[],
        context?: any
    ): Promise<void> => {
        const activeStream = recoveredStreamRef.current ?? stream;

        if (!activeStream) {
            const message = "Speech recording is not ready";

            debugLog("start:not-ready", {
                intendedWord,
                context,
                hasStream: !!activeStream
            });

            setError(message);
            setIsRecording(false);
            if (globalSettings.enableSttDiagnostics) {
                appInsights.trackTrace({
                    message: JSON.stringify({
                        text: "error startSpeechToText not ready",
                        context,
                        hasStream: !!activeStream
                    }),
                    severityLevel: SeverityLevel.Information
                });
            }

            throw new Error(message);
        }

        try {
            lifecycleRef.current = "starting";
            cleanupActiveRecordingSession();
            stopRequestedRef.current = false;
            activeRecordingAttemptId.current = getRecordingAttemptId(context);

            if (recorderRef.current && recorderStreamRef.current !== activeStream) {
                debugLog("recorder:stream-changed", {
                    intendedWord,
                    context,
                    previousStreamId: recorderStreamRef.current?.id,
                    nextStreamId: activeStream.id
                });
                await destroyActiveRecorder();
            }

            let recorder = recorderRef.current;
            let recorderState = "reused";

            if (recorder) {
                await resetActiveRecorder(recorder, context);
            } else {
                recorder = createSpeechRecorder(activeStream, context);
                recorderState = "new";

                if (recorder) {
                    recorderRef.current = recorder;
                    recorderStreamRef.current = activeStream;
                    debugLog("recorder:create", {
                        intendedWord,
                        context,
                        streamId: activeStream.id,
                        recorderType: getRecorderType(recorder)
                    });
                }
            }

            if (!recorder) {
                throw new Error("Speech recorder could not be created");
            }

            debugLog("start:begin", {
                intendedWord,
                explicitContext,
                context,
                recorderState,
                recorderType: getRecorderType(recorder),
                lifecycle: lifecycleRef.current,
                streamId: activeStream.id,
                streamActive: activeStream.active,
                streamTrackStates: activeStream.getAudioTracks().map((track) => ({
                    enabled: track.enabled,
                    muted: track.muted,
                    readyState: track.readyState
                }))
            });

            await withTimeout(recorder.startRecording(), RECORDER_OPERATION_TIMEOUT, "Recorder start timeout");
            lifecycleRef.current = "recording";

            mediaStream.current = activeStream;

            debugLog("start:recorder-started", {
                intendedWord,
                context,
                recorderState: await recorder.getState(),
                recorderType: getRecorderType(recorder),
                lifecycle: lifecycleRef.current,
                audioContextState: audioContextRef.current?.state,
                streamId: mediaStream.current.id,
                streamActive: mediaStream.current.active
            });

            const audioContextReady = await resumeAudioContextIfNeeded(context);

            if (!audioContextReady) {
                throw new Error("AudioContext could not be resumed");
            }

            debugLog("start:recording", {
                intendedWord,
                context,
                recorderState: await recorder.getState(),
                recorderType: getRecorderType(recorder),
                lifecycle: lifecycleRef.current,
                audioContextState: audioContextRef.current?.state,
                streamId: mediaStream.current.id,
                streamActive: mediaStream.current.active,
                streamTrackStates: mediaStream.current.getAudioTracks().map((track) => ({
                    enabled: track.enabled,
                    muted: track.muted,
                    readyState: track.readyState
                }))
            });

            debugLog("hark:create:before", {
                intendedWord,
                context,
                audioContextState: audioContextRef.current?.state,
                streamId: mediaStream.current.id,
                streamActive: mediaStream.current.active
            });
            const speechEvents = Hark(mediaStream.current, {
                audioContext: audioContextRef.current as AudioContext,
                threshold: stoppedTalkingThreshold
            });
            speechEventsRef.current = speechEvents;
            debugLog("hark:create:after", {
                intendedWord,
                context,
                audioContextState: audioContextRef.current?.state
            });

            handleVolume(activeStream);
            setIsRecording(true);

            speechEvents.on("speaking", () => {
                debugLog("hark:speaking", { intendedWord, context });
                if (onStartSpeaking) onStartSpeaking();
            });

            if (testOnly) {
                speechEvents.on("volume_change", (c) => {
                    setCurrentDecibels(c);
                });
            }
            if (useStopTalking) {
                speechEvents.on("stopped_speaking", async () => {
                    if (testOnly) return;

                    if (!isCurrentRecordingAttempt(context)) {
                        debugLog("hark:stale-stopped-speaking", {
                            intendedWord,
                            context,
                            activeRecordingAttemptId: activeRecordingAttemptId.current
                        });
                        return;
                    }

                    debugLog("hark:stopped-speaking", { intendedWord, context });
                    if (onStoppedSpeaking) onStoppedSpeaking();
                    try {
                        const blob = await stopActiveRecorderToBlob({
                            processAudio: true,
                            intendedWord,
                            explicitContext,
                            context
                        });

                        debugLog("hark:blob", {
                            intendedWord,
                            context,
                            blobSize: blob?.size,
                            blobType: blob?.type
                        });
                        if (stopRequestedRef.current) {
                            return;
                        }

                        const canContinueFullAudio = blob && blob.size >= MIN_VALID_AUDIO_BLOB_SIZE;
                        handleBlobToBase64({
                            blob,
                            reset: fullAudio,
                            intendedWord,
                            explicitContext,
                            context
                        });

                        if (fullAudio && canContinueFullAudio && isCurrentRecordingAttempt(context)) {
                            debugLog("full-audio:restart-after-segment", {
                                intendedWord,
                                context,
                                blobSize: blob?.size,
                                blobType: blob?.type
                            });
                            void startSpeechToText(intendedWord, explicitContext, context);
                        }
                    } catch (er) {
                        if (globalSettings.enableSttDiagnostics) {
                            appInsights.trackTrace({
                                message: JSON.stringify({
                                    text: "error startSpeechToText stopped_speaking",
                                    context,
                                    er
                                }),
                                severityLevel: SeverityLevel.Information
                            });
                        }
                    }
                });
            }
        } catch (er) {
            lifecycleRef.current = "error";
            debugLog("start:error", { intendedWord, context, er });
            void destroyActiveRecorder();
            void resetRecorderPermission();
            trackDiagnosticException({
                enabled: globalSettings.enableSttDiagnostics,
                area: STT_DIAGNOSTIC_AREA,
                event: "start:exception",
                error: er,
                properties: {
                    intendedWord,
                    context
                }
            });
            setIsRecording(false);
            setError("Speech recording could not start");
            if (globalSettings.enableSttDiagnostics) {
                appInsights.trackTrace({
                    message: JSON.stringify({
                        text: "error startSpeechToText",
                        context,
                        er
                    }),
                    severityLevel: SeverityLevel.Information
                });
            }

            throw er;
        }
    };

    const stopSpeechToText = async (
        processAudio?: boolean,
        intendedWord?: string,
        explicitContext?: string[],
        context?: any
    ): Promise<boolean> => {
        try {
            stopRequestedRef.current = true;
            const blob = await stopActiveRecorderToBlob({
                processAudio,
                intendedWord,
                explicitContext,
                context
            });

            if (!blob) {
                return !processAudio;
            }

            if (processAudio) {
                return handleBlobToBase64({
                    blob,
                    reset: false,
                    intendedWord,
                    explicitContext,
                    context
                });
            }

            return true;
        } catch (er) {
            debugLog("stop:error", { processAudio, intendedWord, context, er });
            trackDiagnosticException({
                enabled: globalSettings.enableSttDiagnostics,
                area: STT_DIAGNOSTIC_AREA,
                event: "stop:exception",
                error: er,
                properties: {
                    processAudio,
                    intendedWord,
                    context
                }
            });
            if (globalSettings.enableSttDiagnostics) {
                appInsights.trackTrace({
                    message: JSON.stringify({
                        text: "error stopSpeechToText",
                        context,
                        er
                    }),
                    severityLevel: SeverityLevel.Information
                });
            }

            return false;
        }
    };

    const handleBlobToBase64 = ({
        blob,
        reset = false,
        intendedWord,
        explicitContext = [],
        context = {}
    }: {
        blob?: Blob;
        reset?: boolean;
        intendedWord?: string;
        explicitContext?: string[];
        context?: any;
    }): boolean => {
        if (!blob || blob.size < 1 || testOnly) {
            debugLog("process:skip-empty-blob", {
                intendedWord,
                context,
                blobSize: blob?.size,
                testOnly
            });
            return false;
        }

        if (blob.size < MIN_VALID_AUDIO_BLOB_SIZE) {
            debugLog("process:skip-tiny-blob", {
                intendedWord,
                context,
                blobSize: blob.size,
                blobType: blob.type
            });

            setProcessingAttemptId(undefined);
            setIsRecording(false);

            setResults([
                {
                    speechBlob: blob,
                    transcript: "-",
                    elapsed: 0,
                    confidence: 0,
                    intendedWord,
                    context: {
                        ...context,
                        technicalAudioFailure: true
                    },
                    source: SttSourceType.Google,
                    technicalFailure: true
                }
            ]);

            if (fullAudio && !context?.shouldEnd && isCurrentRecordingAttempt(context)) {
                debugLog("process:tiny-blob-restart-without-permission-reset", {
                    intendedWord,
                    context,
                    blobSize: blob.size,
                    blobType: blob.type
                });
                debugLog("full-audio:restart-after-tiny-blob", {
                    intendedWord,
                    context,
                    blobSize: blob.size,
                    blobType: blob.type
                });
                void startSpeechToText(intendedWord, explicitContext, context);
            } else if (!fullAudio) {
                void resetRecorderPermission().then((newStream) => {
                    recoveredStreamRef.current = newStream;

                    debugLog("process:tiny-blob-reset-permission", {
                        intendedWord,
                        context,
                        blobSize: blob.size,
                        blobType: blob.type
                    });
                });
            }

            return false;
        }

        const recordingAttemptId = getRecordingAttemptId(context);

        if (recordingAttemptId && processedRecordingAttemptIds.current.has(recordingAttemptId)) {
            debugLog("process:duplicate-skip", {
                intendedWord,
                context,
                reset,
                blobSize: blob.size,
                blobType: blob.type
            });
            return true;
        }

        if (recordingAttemptId) {
            processedRecordingAttemptIds.current.add(recordingAttemptId);
        }

        debugLog("process:begin", {
            intendedWord,
            explicitContext,
            context,
            lifecycle: lifecycleRef.current,
            reset,
            blobSize: blob.size,
            blobType: blob.type
        });
        if (!reset) {
            lifecycleRef.current = "processing";
        }
        setProcessingAttemptId(recordingAttemptId);

        const reader = new FileReader();
        reader.onerror = () => {
            debugLog("process:file-reader-error", {
                intendedWord,
                context,
                error: reader.error?.message
            });

            if (!isCurrentRecordingAttempt(context)) {
                return;
            }

            setResults([
                {
                    speechBlob: blob,
                    transcript: "-",
                    elapsed: 0,
                    confidence: 0,
                    intendedWord,
                    context,
                    source: SttSourceType.Google
                }
            ]);
            setProcessingAttemptId(undefined);
            if (!reset) {
                lifecycleRef.current = "error";
                setIsRecording(false);
            }
        };
        reader.readAsDataURL(blob);

        reader.onloadend = async () => {
            if (!isCurrentRecordingAttempt(context)) {
                debugLog("process:stale-result", {
                    intendedWord,
                    context,
                    activeRecordingAttemptId: activeRecordingAttemptId.current
                });
                return;
            }

            const start = Date.now();

            const base64data = reader.result as string;

            const audio = { content: "" };
            const blobType = blob.type.toLowerCase();
            const nativeRecorderDecision = getAndroidNativeRecorderDecision();
            const isNativeWebmOpus =
                nativeRecorderDecision.useNativeRecorder &&
                (blobType === ANDROID_WEBM_OPUS_MIME_TYPE || blobType.includes("webm"));

            const config: GoogleCloudRecognitionConfig = {
                encoding: "LINEAR16",
                languageCode: "en-US",
                sampleRateHertz: 16000,
                ...googleCloudRecognitionConfig
            };

            const languageCode = config.languageCode ?? "en-US";

            if (explicitContext.length > 0) {
                let boost = 10;
                if (!isEmpty(googleCloudRecognitionConfig?.speechContexts)) {
                    boost = Number(googleCloudRecognitionConfig?.speechContexts![0].boost);
                }

                config.speechContexts = [{ boost, phrases: explicitContext }];
            }

            if (isNativeWebmOpus) {
                config.encoding = "WEBM_OPUS";
                config.sampleRateHertz = 48000;
            }

            const data = {
                config,
                audio
            };

            // Gets raw base 64 string data
            audio.content = base64data.substring(base64data.indexOf(",") + 1);

            // create app insight log
            const currentFlow = assessmentFlow.currentFlow - 1;
            const flowName = currentFlow >= 0 ? assessmentFlow.flows[currentFlow].name : "Practice";

            const transaction = {
                assessmentId: assessment.id,
                assessmentFlow: flowName,
                payloadStt: {
                    config: {
                        encoding: config.encoding,
                        languageCode: config.languageCode,
                        sampleRateHertz: config.sampleRateHertz,
                        model: config.model,
                        useEnhanced: config.useEnhanced,
                        maxAlternatives: config.maxAlternatives,
                        speechContextCount: config.speechContexts?.length ?? 0,
                        phraseCount: config.speechContexts?.[0]?.phrases?.length ?? 0,
                        boost: config.speechContexts?.[0]?.boost
                    },
                    audioContentLength: audio.content.length,
                    blobSize: blob.size,
                    blobType: blob.type
                },
                statusCode: 0,
                statusText: "",
                responseStt: "",
                azureResponseStt: ""
            };
            const emptyResult = {
                speechBlob: blob,
                transcript: "-",
                elapsed: Date.now() - start,
                confidence: 0,
                intendedWord,
                context,
                source: SttSourceType.Google
            };

            try {
                // create fetch crontroller
                let timeout = 100 * 1000;

                if (flowName.includes("Present Targets")) {
                    timeout = globalSettings.ptTimeout * 1000;
                } else if (flowName.includes("Free Recall")) {
                    timeout = globalSettings.frTimeout * 1000;
                } else if (flowName.includes("Cued Recall")) {
                    timeout = globalSettings.crTimeout * 1000;
                }

                debugLog("stt:request", {
                    intendedWord,
                    context,
                    flowName,
                    timeout,
                    googlePhraseCount: config.speechContexts?.[0]?.phrases?.length ?? 0,
                    boost: config.speechContexts?.[0]?.boost,
                    googleEncoding: config.encoding,
                    audioContentLength: audio.content.length,
                    azureSkipped: isNativeWebmOpus
                });

                const controller = new AbortController();
                const reason = new DOMException("STT timeout", "TimeoutError");
                const timeoutId = setTimeout(() => controller.abort(reason), timeout);

                // Google fetch
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/promise-function-async
                const googleRequest = () =>
                    fetch(`https://speech.googleapis.com/v1p1beta1/speech:recognize?key=${googleApiKey}`, {
                        method: "POST",
                        body: JSON.stringify(data),
                        signal: controller.signal
                    });

                // Azure fetch
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/promise-function-async
                const azureRequest = () =>
                    fetch(
                        `https://${azureRegion}.stt.speech.microsoft.com/speech/recognition/conversation/cognitiveservices/v1?language=${languageCode}&format=detailed`,
                        {
                            method: "POST",
                            headers: {
                                "Ocp-Apim-Subscription-Key": azureApiKey,
                                "Content-Type": "audio/wav"
                            },
                            body: blob,
                            signal: controller.signal
                        }
                    );
                const azurePromise = isNativeWebmOpus
                    ? Promise.resolve({ response: undefined, json: {}, skipped: true })
                    : azureRequest().then(async (response) => ({
                          response,
                          json: await response.json().catch((parseFailure) => ({ parseError: `${parseFailure}` })),
                          skipped: false
                      }));

                const [googleResult, azureResultSettled] = await Promise.allSettled([
                    googleRequest().then(async (response) => ({
                        response,
                        json: await response.json().catch((parseFailure) => ({ parseError: `${parseFailure}` }))
                    })),
                    azurePromise
                ]);

                clearTimeout(timeoutId);

                const googleResponse = googleResult.status === "fulfilled" ? googleResult.value.response : undefined;
                const googleJson = googleResult.status === "fulfilled" ? googleResult.value.json : {};
                const azureResponse =
                    azureResultSettled.status === "fulfilled" ? azureResultSettled.value.response : undefined;
                const azureJson = azureResultSettled.status === "fulfilled" ? azureResultSettled.value.json : {};
                const googleError = googleResult.status === "rejected" ? googleResult.reason : undefined;
                const azureError = azureResultSettled.status === "rejected" ? azureResultSettled.reason : undefined;
                const azureSkipped = Boolean(
                    azureResultSettled.status === "fulfilled" && azureResultSettled.value.skipped
                );
                const allRequestedProvidersFailed = !!googleError && (azureSkipped || !!azureError);

                debugLog("stt:response", {
                    intendedWord,
                    context,
                    flowName,
                    elapsed: Date.now() - start,
                    googleEncoding: config.encoding,
                    googleStatus: googleResponse?.status,
                    googleStatusText: googleResponse?.statusText,
                    googleError: googleError ? `${googleError}` : undefined,
                    googleParseError: googleJson.parseError,
                    googleResultCount: googleJson.results?.length ?? 0,
                    googleTranscripts: flatten(googleJson?.results?.map((x: any) => x.alternatives))
                        .filter((x) => !isEmpty(x))
                        .map((x: any) => ({
                            transcript: x.transcript,
                            confidence: x.confidence
                        })),
                    azureSkipped,
                    azureStatus: azureResponse?.status,
                    azureStatusText: azureResponse?.statusText,
                    azureError: azureError ? `${azureError}` : undefined,
                    azureParseError: azureJson.parseError,
                    azureDisplayText: azureJson.DisplayText,
                    azureNBestCount: azureJson.NBest?.length ?? 0,
                    azureTranscripts: azureJson.NBest?.map((x: { Lexical: string; Confidence: number }) => ({
                        transcript: x.Lexical,
                        confidence: x.Confidence
                    }))
                });

                if (googleError || azureError) {
                    debugLog("stt:provider-error", {
                        intendedWord,
                        context,
                        googleError: googleError ? `${googleError}` : undefined,
                        azureError: azureError ? `${azureError}` : undefined,
                        azureSkipped
                    });
                }

                if (allRequestedProvidersFailed) {
                    const errorMessage = `${googleError}`;

                    if (errorMessage.includes("STT timeout") || errorMessage.includes("TimeoutError")) {
                        dispatch(updateInternetStatus({ status: InternetStatus.Timeout }));
                    } else if (errorMessage.includes("Failed to fetch")) {
                        dispatch(updateInternetStatus({ status: InternetStatus.Disconected }));
                    }
                }

                transaction.statusCode = googleResponse?.status ?? 0;
                transaction.statusText = googleResponse?.statusText ?? (googleError ? `${googleError}` : "");
                transaction.responseStt = summarizeSttResponse(googleJson);
                transaction.azureResponseStt = summarizeAzureResponse(azureJson);

                logAppInsight(transaction);

                if (allRequestedProvidersFailed) {
                    debugLog("stt:all-providers-failed-empty-result", {
                        intendedWord,
                        context
                    });
                    setResults([emptyResult]);
                    setProcessingAttemptId(undefined);
                    if (!reset) {
                        lifecycleRef.current = "idle";
                        setIsRecording(false);
                    }
                    return;
                }

                // Update results state with transcribed text
                if (isEmpty(googleJson.results) && (azureSkipped || (azureJson.DisplayText ?? "") === "")) {
                    setResults([emptyResult]);
                } else {
                    const flattenAlternatives = flatten(googleJson?.results?.map((x: any) => x.alternatives)).filter(
                        (x) => !isEmpty(x)
                    );

                    const googleSttResults = flattenAlternatives.map((alternative: any) => ({
                        speechBlob: blob,
                        transcript: alternative.transcript.replace(".", ""),
                        elapsed: Date.now() - start,
                        confidence: alternative.confidence,
                        intendedWord,
                        context,
                        source: SttSourceType.Google
                    }));

                    const azureTranscript = flatten(azureJson.NBest?.map((x: { Lexical: string }) => x.Lexical))
                        .join(" ")
                        .trim();

                    const azureResult = azureSkipped
                        ? undefined
                        : {
                              speechBlob: blob,
                              transcript: azureTranscript,
                              elapsed: Date.now() - start,
                              confidence: azureJson.NBest?.[0]?.Confidence ?? 0,
                              intendedWord,
                              context,
                              source: SttSourceType.Azure
                          };

                    if (
                        isEmpty(googleSttResults) &&
                        (azureResult?.transcript ?? "") === "" &&
                        (context?.shouldEnd || !context?.stepName?.includes("Free Recall"))
                    ) {
                        setResults(azureResult ? [emptyResult, azureResult] : [emptyResult]);
                    } else {
                        const gr = isEmpty(googleSttResults) ? [emptyResult] : googleSttResults;
                        setResults(azureResult ? [...gr, azureResult] : gr);
                    }
                }
                setProcessingAttemptId(undefined);

                if (!reset) {
                    lifecycleRef.current = "idle";
                    setIsRecording(false);
                }
            } catch (err: any) {
                debugLog("stt:error", {
                    intendedWord,
                    context,
                    message: err?.message,
                    name: err?.name
                });
                trackDiagnosticException({
                    enabled: globalSettings.enableSttDiagnostics,
                    area: STT_DIAGNOSTIC_AREA,
                    event: "stt:exception",
                    error: err,
                    properties: {
                        intendedWord,
                        context,
                        message: err?.message,
                        name: err?.name
                    }
                });
                if (err.message === "STT timeout") {
                    dispatch(updateInternetStatus({ status: InternetStatus.Timeout }));
                } else if (err.message === "Failed to fetch") {
                    dispatch(updateInternetStatus({ status: InternetStatus.Disconected }));
                }
                setResults([emptyResult]);
                setProcessingAttemptId(undefined);
                if (!reset) {
                    lifecycleRef.current = "error";
                    setIsRecording(false);
                }
            }
        };

        return true;
    };

    const handleVolume = (volumeStream?: MediaStream): void => {
        if (!volumeStream) return;

        cleanupVolumeMonitor();

        debugLog("volume-monitor:start", {
            streamId: volumeStream.id,
            streamActive: volumeStream.active,
            audioContextState: audioContextRef.current?.state,
            streamTrackStates: volumeStream.getAudioTracks().map((track) => ({
                enabled: track.enabled,
                muted: track.muted,
                readyState: track.readyState
            }))
        });

        const input = audioContextRef.current!.createMediaStreamSource(volumeStream);
        const analyserNode = audioContextRef.current!.createAnalyser();
        volumeInputRef.current = input;
        volumeAnalyserRef.current = analyserNode;

        analyserNode.smoothingTimeConstant = 0.8;
        analyserNode.fftSize = 1024;

        input.connect(analyserNode);
        setVol(0);

        const onFrame = (): void => {
            const array = new Uint8Array(analyserNode.frequencyBinCount);
            analyserNode.getByteFrequencyData(array);
            const averageVolume = getAverageVolume(array);
            setVol(averageVolume);
            volumeAnimationFrameRef.current = window.requestAnimationFrame(onFrame);
        };

        volumeAnimationFrameRef.current = window.requestAnimationFrame(onFrame);
    };

    const handleDestroy = (): void => {
        lifecycleRef.current = "destroyed";
        cleanupActiveRecordingSession();
        stream?.getTracks().forEach((track) => track.stop());
        if (recoveredStreamRef.current !== stream) {
            recoveredStreamRef.current?.getTracks().forEach((track) => track.stop());
        }
        recoveredStreamRef.current = undefined;
        void destroyActiveRecorder();
    };

    const hardResetAudioPipeline = async (context?: any): Promise<boolean> => {
        const preserveAudioContext = context?.preserveAudioContext === true;

        debugLog("audio-pipeline:hard-reset:start", {
            context,
            audioContextState: audioContextRef.current?.state,
            streamId: recoveredStreamRef.current?.id ?? stream?.id,
            streamActive: recoveredStreamRef.current?.active ?? stream?.active,
            streamTrackStates: recoveredStreamRef.current?.getAudioTracks() ?? stream?.getAudioTracks()
        });

        try {
            lifecycleRef.current = "idle";
            stopRequestedRef.current = true;
            activeRecordingAttemptId.current = undefined;
            processedRecordingAttemptIds.current.clear();

            cleanupActiveRecordingSession();
            await destroyActiveRecorder();

            stream?.getTracks().forEach((track) => track.stop());
            if (recoveredStreamRef.current !== stream) {
                recoveredStreamRef.current?.getTracks().forEach((track) => track.stop());
            }
            recoveredStreamRef.current = undefined;

            if (!preserveAudioContext && audioContextRef.current && audioContextRef.current.state !== "closed") {
                await audioContextRef.current.close();
            }

            if (!audioContextRef.current || audioContextRef.current.state === "closed") {
                audioContextRef.current = createAudioContext();
            }

            const newStream = await resetRecorderPermission();
            recoveredStreamRef.current = newStream;

            debugLog("audio-pipeline:hard-reset:success", {
                context,
                audioContextState: audioContextRef.current?.state,
                streamId: newStream?.id,
                streamActive: newStream?.active,
                streamTrackStates: newStream?.getAudioTracks()
            });

            return !!newStream;
        } catch (er) {
            debugLog("audio-pipeline:hard-reset:error", {
                context,
                er
            });
            trackDiagnosticException({
                enabled: globalSettings.enableSttDiagnostics,
                area: STT_DIAGNOSTIC_AREA,
                event: "audio-pipeline:hard-reset:exception",
                error: er,
                properties: {
                    context
                }
            });

            return false;
        } finally {
            stopRequestedRef.current = false;
        }
    };

    function getAverageVolume(array: Uint8Array): number {
        let amplitudeSum = 0;

        for (let i = 0; i < array.length; i++) {
            amplitudeSum += array[i] * array[i];
        }

        return Math.sqrt(amplitudeSum / array.length) / 100;
    }

    const logAppInsight = (transaction: ITransaction): void => {
        if (!globalSettings.enableSttDiagnostics) {
            return;
        }

        const input = JSON.stringify(transaction);
        const splitNumber = 30000;
        const data = {
            assessmentId: transaction.assessmentId,
            assessmentFlow: transaction.assessmentFlow,
            statusCode: transaction.statusCode,
            statusText: transaction.statusText,
            id: uuidv4(),
            part: 0,
            text: ""
        };

        if (input.length <= splitNumber) {
            appInsights.trackTrace({
                message: input,
                severityLevel: SeverityLevel.Information
            });
        } else {
            for (let i = 0; i < input.length; i += splitNumber) {
                data.part++;
                data.text = input.slice(i, i + splitNumber);

                appInsights.trackTrace({
                    message: JSON.stringify(data),
                    severityLevel: SeverityLevel.Information
                });
            }
        }
    };

    const summarizeSttResponse = (response: any): any => ({
        parseError: response?.parseError,
        resultCount: response?.results?.length ?? 0,
        transcripts: flatten(response?.results?.map((x: any) => x.alternatives))
            .filter((x) => !isEmpty(x))
            .map((x: any) => ({
                transcript: x.transcript,
                confidence: x.confidence
            }))
    });

    const summarizeAzureResponse = (response: any): any => ({
        parseError: response?.parseError,
        recognitionStatus: response?.RecognitionStatus,
        displayText: response?.DisplayText,
        nBestCount: response?.NBest?.length ?? 0,
        transcripts: response?.NBest?.map((x: { Lexical: string; Confidence: number }) => ({
            transcript: x.Lexical,
            confidence: x.Confidence
        }))
    });

    return {
        error,
        isRecording,
        results,
        isReady: !!stream,
        processingAttemptId,
        recordingVolume: vol,
        currentDecibels,
        stream,
        startSpeechToText,
        stopSpeechToText,
        destroy: handleDestroy,
        hardResetAudioPipeline
    };
};
